import { useCallback, useState } from 'react';
import { isArray, sumBy } from 'lodash';
import { Card, CardBody, CardHeader, Icon } from '@repo/ui';
import { dateUtil, numberUtil } from '@xtnd/web/shared';
import { DataTable, DataTableColumn } from '@xtnd/web/shared/components';

import { Bill } from '../../types';

interface BillTableProps {
  data: Bill[];
  isLoading: boolean;
  error: Error | null;
}

export const BillsTable = ({ data, isLoading, error }: BillTableProps) => {
  const [isExpand, setIsExpand] = useState<boolean>(true);
  const columns: DataTableColumn<Bill>[] = [
    {
      key: 'number',
      header: 'Number',
      align: 'left',
      headerClassName: 'rounded-none',
      renderCell: bill => {
        return (
          <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            {bill.number}
          </span>
        );
      },
    },
    {
      key: 'billDate',
      header: 'Date',
      align: 'left',
      renderCell: bill => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {dateUtil.formatDate(new Date(bill.date), dateUtil.DateFormat.US)}
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      renderCell: bill => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {numberUtil.formatCurrency(bill.total)}
        </span>
      ),
    },
    {
      key: 'fees',
      header: 'Fees',
      align: 'right',
      renderCell: bill => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {numberUtil.formatCurrency(bill.fees)}
        </span>
      ),
    },
    {
      key: 'expenses',
      header: 'Expenses',
      align: 'right',
      renderCell: bill => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {numberUtil.formatCurrency(bill.expenses)}
        </span>
      ),
    },
    {
      key: 'arTotal',
      header: 'AR total',
      align: 'right',
      renderCell: bill => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {numberUtil.formatCurrency(bill.arTotal)}
        </span>
      ),
    },
    {
      key: 'arFees',
      header: 'AR Fees',
      align: 'right',
      renderCell: bill => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {numberUtil.formatCurrency(bill.arFees)}
        </span>
      ),
    },
    {
      key: 'arExpenses',
      header: 'AR Expenses',
      align: 'right',
      renderCell: bill => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {numberUtil.formatCurrency(bill.arExpenses)}
        </span>
      ),
    },
  ];

  const renderHeader = () => (
    <button
      className="flex items-center justify-between w-full"
      aria-label={isExpand ? 'Collapse bills' : 'Expand bills'}
      onClick={() => setIsExpand(prev => !prev)}
    >
      <div className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">Bills</div>
      <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light cursor-pointer">
        {isExpand ? <Icon name="HiChevronDown" /> : <Icon name="HiChevronRight" />}
      </span>
    </button>
  );

  const calculateTotals = (rows: Bill[]) => {
    if (!isArray(rows)) return null;

    return {
      total: sumBy(rows, 'total'),
      fees: sumBy(rows, 'fees'),
      expenses: sumBy(rows, 'expenses'),
      arTotal: sumBy(rows, 'arTotal'),
      arFees: sumBy(rows, 'arFees'),
      arExpenses: sumBy(rows, 'arExpenses'),
    };
  };

  const renderFooter = useCallback(() => {
    const totals = calculateTotals(data);
    return (
      <tr className="border-b border-r border-gray-200 dark:border-gray-700 bg-xtnd-blue-100">
        <td
          key="totalBils"
          colSpan={2}
          className="px-6 py-2 border-t border-xtnd-dark dark:border-xtnd-dark  dark:bg-xtnd-dark text-sm dark:text-xtnd-light"
        >
          <span className="font-bold">Total Bills</span>
        </td>
        <td
          key="total"
          colSpan={1}
          className="px-6 py-2 border-t border-xtnd-dark dark:border-xtnd-dark  dark:bg-xtnd-dark text-sm dark:text-xtnd-light text-right"
        >
          <span className="font-bold">{numberUtil.formatCurrency(totals?.total || 0)}</span>
        </td>
        <td
          key="fees"
          colSpan={1}
          className="px-6 py-2 border-t border-xtnd-dark dark:border-xtnd-dark  dark:bg-xtnd-dark text-sm dark:text-xtnd-light text-right"
        >
          <span className="font-bold">{numberUtil.formatCurrency(totals?.fees || 0)}</span>
        </td>
        <td
          key="expenses"
          colSpan={1}
          className="px-6 py-2 border-t border-xtnd-dark dark:border-xtnd-dark  dark:bg-xtnd-dark text-sm dark:text-xtnd-light text-right"
        >
          <span className="font-bold">{numberUtil.formatCurrency(totals?.expenses || 0)}</span>
        </td>
        <td
          key="arTotal"
          colSpan={1}
          className="px-6 py-2 border-t border-xtnd-dark dark:border-xtnd-dark  dark:bg-xtnd-dark text-sm dark:text-xtnd-light text-right"
        >
          <span className="font-bold">{numberUtil.formatCurrency(totals?.arTotal || 0)}</span>
        </td>
        <td
          key="arFees"
          colSpan={1}
          className="px-6 py-2 border-t border-xtnd-dark dark:border-xtnd-dark  dark:bg-xtnd-dark text-sm dark:text-xtnd-light text-right"
        >
          <span className="font-bold">{numberUtil.formatCurrency(totals?.arFees || 0)}</span>
        </td>
        <td
          key="arExpenses"
          colSpan={1}
          className="px-6 py-2 border-t border-xtnd-dark dark:border-xtnd-dark  dark:bg-xtnd-dark text-sm dark:text-xtnd-light text-right"
        >
          <span className="font-bold">{numberUtil.formatCurrency(totals?.arExpenses || 0)}</span>
        </td>
      </tr>
    );
  }, [data]);

  return (
    <Card>
      <CardHeader className={`${!isExpand ? '!border-none !mb-0 !pb-0' : ''}`}>
        {renderHeader()}
      </CardHeader>
      {isExpand && (
        <CardBody>
          <DataTable
            columns={columns}
            data={{
              data: data || [],
              total: data?.length,
            }}
            isLoading={isLoading}
            error={error}
            tableContainerClassName="overflow-y-auto relative"
            tableClassName="min-w-full divide-y divide-gray-200 relative"
            stickyHeader={true}
            loadingRowCount={5}
            loadingColumnCount={7}
            renderFooter={renderFooter}
          />
        </CardBody>
      )}
    </Card>
  );
};
